import { useState, useRef, useEffect } from "react";
import { db, storage, functions } from "../firebase";
import { doc, setDoc, updateDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { C, card } from "../styles";
import { Btn } from "../ui";
import { ALL_SLOTS, validateSlotFile, slotTypeError, slotIcon, suggestSlot } from "../slotConfig";
import { genSessionId, buildInitialSlots, projectCode, stripPidPrefix } from "./SessionCreator";

function titleToKey(t) {
  return t.trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_|_$/g, "");
}

function buildRenamedName(title, sessionId, pid, slot, version, original) {
  const ext = original.includes(".") ? "." + original.split(".").pop() : "";
  return `${titleToKey(title)}_${sessionId}_${pid}_${slot.replace(/\s+/g, "_")}_v${version}${ext}`;
}

function buildStoragePath(project, sessionId, pid, title, renamedName) {
  return `projects/${project}/sessions/${sessionId}/${pid}/${titleToKey(title)}/${renamedName}`;
}

const inputStyle = {
  background: C.surface,
  border: "1px solid var(--border-2)",
  borderRadius: "9px",
  padding: "11px 14px",
  color: C.text1,
  fontSize: "14px",
  outline: "none",
  fontFamily: "inherit",
};

function BatchUpload({ sessions, pid, userName, userEmail, projects, onDone, batchUploader = false }) {
  const [files, setFiles] = useState([]);
  const [otherTexts, setOtherTexts] = useState({});
  const [selectedSession, setSelectedSession] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newProject, setNewProject] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileProgress, setFileProgress] = useState({});
  const [newSecondCreatorPid, setNewSecondCreatorPid] = useState("");
  const [secondCreatorError, setSecondCreatorError] = useState("");
  const [shareWithCoCreator, setShareWithCoCreator] = useState(false);
  const [newSubmissionNumber, setNewSubmissionNumber] = useState("");
  const [userEditedNumber, setUserEditedNumber] = useState(false);
  const [slotErrors, setSlotErrors] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [jobMeta, setJobMeta] = useState({});   // index -> resolved job, for retry
  const [retrying, setRetrying] = useState({});  // index -> bool
  const [fileErrors, setFileErrors] = useState({}); // index -> error code/message
  const fileInputRef = useRef();

  const CONCURRENCY = 3; // parallel uploads — sweet spot for Firebase Storage

  // Warn before closing/refreshing the tab while an upload is running —
  // navigating within the app no longer cancels uploads (the component is
  // kept mounted), but a full page unload still would.
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);

  const ownSessions = sessions.filter(s => s.creatorPid === pid);
  // Flagged "Batch Uploader" users can also target Draft sessions created by
  // their partners (where they're an accepted partner) — lets a partner drop
  // GoPro files into many shared sessions from one tab instead of 20.
  const partnerSessions = batchUploader
    ? sessions.filter(s =>
        s.creatorPid !== pid &&
        s.status === "Draft" &&
        (s.partnerPids || []).includes(pid))
    : [];
  const targetSessions = [...ownSessions, ...partnerSessions];
  const chosenSession = targetSessions.find(s => s.sessionId === selectedSession);
  const isNew = selectedSession === "new";
  const doneCount = Object.values(fileProgress).filter(v => v === 100).length;

  const effectiveSlotFor = (i) => {
    const a = files[i].assignedSlot;
    return a === "__other__" ? (otherTexts[i] || "").trim() || "Unlabeled" : a;
  };
  // A file's target session: its own row assignment wins, else the single
  // "Map to Session" selector (unless that's the create-new option).
  const resolveTargetSessionId = (i) =>
    files[i].sessionId || (selectedSession !== "new" ? selectedSession : "");

  const canUpload = files.length > 0 && (
    isNew
      ? (newTitle.trim() && newProject)
      : files.every((_, i) => resolveTargetSessionId(i))
  );
  // Count of files still missing a session target (per-file mode hint).
  const unassignedCount = isNew ? 0 : files.filter((_, i) => !resolveTargetSessionId(i)).length;

  // Best-effort: match a filename to one of the user's target sessions by
  // looking for the session title (normalized) or sessionId inside the name.
  // Returns "" when nothing matches — never guesses wrongly.
  const autoMatchSession = (filename) => {
    if (!batchUploader) return "";
    const norm = filename.toLowerCase().replace(/[^a-z0-9]+/g, "");
    for (const s of targetSessions) {
      const titleKey = (s.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
      if (titleKey && titleKey.length >= 3 && norm.includes(titleKey)) return s.sessionId;
      if (norm.includes(s.sessionId.toLowerCase().replace(/[^a-z0-9]+/g, ""))) return s.sessionId;
    }
    return "";
  };

  const addFiles = (incoming) => {
    const all = Array.from(incoming);
    const empty = all.filter(f => f.size === 0);
    if (empty.length) {
      alert(`Skipped ${empty.length} empty (0-byte) file(s): ${empty.map(f => f.name).join(", ")}.\n\nIf they live in iCloud/Drive, download them to your device first, then re-add.`);
    }
    const entries = all.filter(f => f.size > 0).map(f => ({
      file: f,
      assignedSlot: suggestSlot(f.name),
      suggestedSlot: suggestSlot(f.name),
      sessionId: autoMatchSession(f.name), // per-file target (flagged users)
    }));
    if (entries.length) setFiles(prev => [...prev, ...entries]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  // Uploads a single resolved job: { index, file, session, slot, version }.
  // Writes progress to fileProgress[index] (0–100, or -1 on failure).
  const uploadOne = async (job) => {
    const { index, file, session, slot, version } = job;
    const isUnlabeled = slot === "Unlabeled";
    try {
      const renamedName = isUnlabeled
        ? file.name
        : buildRenamedName(session.title, session.sessionId, pid, slot, version, file.name);
      const storageFileName = isUnlabeled ? `${Date.now()}_${file.name}` : renamedName;
      const storagePath = buildStoragePath(session.project, session.sessionId, pid, session.title, storageFileName);

      const url = await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(ref(storage, storagePath), file);
        task.on("state_changed",
          snap => setFileProgress(p => ({ ...p, [index]: Math.round(snap.bytesTransferred / snap.totalBytes * 100) })),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });

      const newFile = {
        originalName: file.name,
        renamedName,
        url,
        ...(version !== null && { versionNumber: version }),
        sizeBytes: file.size,
        uploadedBy: { pid, name: userName },
        uploadedAt: new Date().toISOString(),
      };

      const docRef = doc(db, "projects", session.project, "sessions", session.sessionId);
      const updates = {
        [`slots.${slot}.history`]: arrayUnion(newFile),
        updatedAt: serverTimestamp(),
      };
      if (!isUnlabeled) updates[`slots.${slot}.activeFile`] = newFile;
      await updateDoc(docRef, updates);

      setFileProgress(p => ({ ...p, [index]: 100 }));
      setFileErrors(e => { const n = { ...e }; delete n[index]; return n; });
      return true;
    } catch (err) {
      console.error("Upload failed:", file.name, err);
      setFileProgress(p => ({ ...p, [index]: -1 }));
      setFileErrors(e => ({ ...e, [index]: err?.code || err?.message || "upload failed" }));
      return false;
    }
  };

  // Runs jobs with bounded concurrency (N workers pulling from a shared index).
  const runQueue = async (jobs, concurrency) => {
    let next = 0;
    const worker = async () => {
      while (next < jobs.length) {
        const job = jobs[next++];
        await uploadOne(job);
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, jobs.length) }, worker));
  };

  const handleUploadAll = async () => {
    if (!canUpload || uploading) return;
    setSecondCreatorError("");

    // Validate co-creator PID before starting upload (also used for share).
    const trimmedSecond = stripPidPrefix(newSecondCreatorPid) || null;
    let resolvedSecond = trimmedSecond;
    if (isNew && trimmedSecond) {
      const validatePID = httpsCallable(functions, "validatePID");
      try {
        const { data } = await validatePID({ pid: trimmedSecond });
        resolvedSecond = data.pid;
      } catch (err) {
        setSecondCreatorError(err.message || "Co-creator PID not found.");
        return;
      }
    }
    const resolvedPartner = shareWithCoCreator ? resolvedSecond : null;

    setUploading(true);
    setFileProgress({});

    // Build a lookup of every session a file might target.
    const sessionMap = {};
    for (const s of targetSessions) sessionMap[s.sessionId] = s;

    // Create-new flow: make one session and route all files to it.
    let forcedSessionId = null;
    if (isNew) {
      const sessionId = genSessionId(pid);
      const sessionDoc = {
        sessionId,
        title: newTitle.trim(),
        project: newProject,
        creatorPid: pid,
        creatorName: userName,
        creatorEmail: userEmail,
        partnerPids: [],
        pendingPartnerPids: resolvedPartner ? [resolvedPartner] : [],
        sharedWith: resolvedPartner ? [resolvedPartner] : [],
        secondCreatorPid: resolvedSecond || null,
        submissionNumber: newSubmissionNumber ? parseInt(newSubmissionNumber, 10) : null,
        status: "Draft",
        slots: buildInitialSlots(),
        notionPageId: null,
      };
      const docRef = doc(db, "projects", newProject, "sessions", sessionId);
      await setDoc(docRef, { ...sessionDoc, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      sessionMap[sessionId] = sessionDoc;
      forcedSessionId = sessionId;
    }

    // Build jobs with version numbers pre-assigned per (session, slot) so
    // concurrent uploads to the same slot don't collide on version.
    const verCounter = {};
    const jobs = files.map((f, i) => {
      const targetId = forcedSessionId || resolveTargetSessionId(i);
      const session = sessionMap[targetId];
      const slot = effectiveSlotFor(i);
      const key = `${targetId}::${slot}`;
      if (verCounter[key] === undefined) verCounter[key] = (session.slots?.[slot]?.history || []).length;
      const version = slot === "Unlabeled" ? null : (++verCounter[key]);
      return { index: i, file: f.file, session, slot, version };
    });

    // Stash jobs for per-file retry.
    setJobMeta(jobs.reduce((m, j) => { m[j.index] = j; return m; }, {}));

    await runQueue(jobs, CONCURRENCY);

    setUploading(false);

    // If everything landed in one session, open it; otherwise stay put so the
    // user can review the multi-session result.
    const distinctSessions = [...new Set(jobs.map(j => j.session.sessionId))];
    if (distinctSessions.length === 1) {
      onDone(jobs[0].session.project, distinctSessions[0]);
    }
  };

  // Re-upload a single failed file. Recomputes version from the live session
  // so a retry after a partial failure doesn't duplicate or skip a version.
  const retryFile = async (i) => {
    const job = jobMeta[i];
    if (!job || retrying[i]) return;
    setRetrying(r => ({ ...r, [i]: true }));
    const live = targetSessions.find(s => s.sessionId === job.session.sessionId) || job.session;
    const freshVersion = job.slot === "Unlabeled"
      ? null
      : (live.slots?.[job.slot]?.history || []).length + 1;
    setFileProgress(p => ({ ...p, [i]: 0 }));
    await uploadOne({ ...job, session: live, version: freshVersion });
    setRetrying(r => ({ ...r, [i]: false }));
  };

  return (
    <div style={{ maxWidth: "720px" }}>

      {/* ── How it works ── */}
      <div style={{ display: "flex", gap: "0", marginBottom: "20px", border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}>
        {(batchUploader
          ? [
            { step: "1", icon: "📂", title: "Drop your files", desc: "Drop files for one session or many at once — slot labels are auto-matched (GoPro → Video Primary)." },
            { step: "2", icon: "🎯", title: "Assign sessions", desc: "Each row has a session dropdown (yours + shared). Filenames auto-match where they can — fix any that don't." },
            { step: "3", icon: "🚀", title: "Upload in parallel", desc: "All files upload 3 at a time across every target session. Any that fail get a one-click Retry." },
          ]
          : [
            { step: "1", icon: "📂", title: "Drop your files", desc: "Filenames are auto-matched to slots (GoPro → Video Primary, etc.)" },
            { step: "2", icon: "🏷️", title: "Review labels", desc: "Adjust any slot assignments before uploading — use the dropdown on each row." },
            { step: "3", icon: "🚀", title: "Select a session", desc: "Link to an existing draft or create a new session — all files upload in one go." },
          ]
        ).map(({ step, title, desc }, i, arr) => (
          <div key={step} style={{ flex: 1, padding: "14px 16px", borderRight: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
              <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--og-accent)", background: "var(--primary-soft)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "999px", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{step}</span>
              <span style={{ fontSize: "12px", fontWeight: "600", color: C.text1 }}>{title}</span>
            </div>
            <p style={{ fontSize: "12px", color: C.text2, margin: 0, lineHeight: "1.6" }}>{desc}</p>
          </div>
        ))}
      </div>

      {/* ── Drop zone / file list ── */}
      <div
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        style={{
          ...card,
          border: dragOver
            ? "2px dashed rgba(99,102,241,0.6)"
            : files.length ? "1px solid var(--border)" : "2px dashed var(--border)",
          padding: "28px",
          marginBottom: "16px",
          background: dragOver ? "rgba(99,102,241,0.04)" : undefined,
          transition: "all 0.2s",
          cursor: files.length ? "default" : "pointer",
        }}
        onClick={() => !files.length && fileInputRef.current.click()}
      >
        {!files.length ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "15px", fontWeight: "600", color: C.text1, marginBottom: "6px" }}>
              {dragOver ? "Drop your files here" : "Drag & drop your files"}
            </div>
            <div style={{ fontSize: "13px", color: C.text2, marginBottom: "10px" }}>
              Slot labels are suggested automatically — adjust before uploading
            </div>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--og-accent)", cursor: "pointer" }}>or browse files</span>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <span style={{ fontSize: "13px", fontWeight: "600", color: C.text1 }}>
                {files.length} file{files.length !== 1 ? "s" : ""} queued
              </span>
              {!uploading && (
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    onClick={() => fileInputRef.current.click()}
                    style={{ fontSize: "12px", color: "var(--og-accent)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                  >
                    + Add more
                  </button>
                  <button
                    onClick={() => setFiles([])}
                    style={{ fontSize: "12px", color: C.text3, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "8px", overflow: "hidden", maxHeight: "320px", overflowY: "auto" }}>
              {files.map((pf, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 14px",
                  borderBottom: i < files.length - 1 ? "1px solid var(--surface-2)" : "none",
                  background: "rgba(255,255,255,0.01)",
                }}>
                  <div style={{ width: "32px", height: "32px", borderRadius: "7px", background: "var(--surface-2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "14px" }}>
                    {(uploading || retrying[i] || fileProgress[i] === 100 || fileProgress[i] === -1)
                      ? fileProgress[i] === 100  ? <span style={{ color: C.success, fontSize: "16px" }}>✓</span>
                        : fileProgress[i] === -1  ? <span style={{ color: C.danger }}>✕</span>
                        : <span style={{ fontSize: "10px", color: C.primary, fontWeight: "700" }}>{fileProgress[i] || 0}%</span>
                      : slotIcon(pf.assignedSlot)
                    }
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13px", color: C.text1, fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {pf.file.name}
                    </div>
                    {fileProgress[i] === -1 && fileErrors[i]
                      ? <div style={{ fontSize: "10px", color: C.danger }}>✕ {fileErrors[i]}</div>
                      : slotErrors[i]
                      ? <div style={{ fontSize: "10px", color: C.danger }}>⚠ {slotErrors[i]}</div>
                      : pf.suggestedSlot !== pf.assignedSlot && <div style={{ fontSize: "10px", color: C.text3 }}>Suggested: {pf.suggestedSlot}</div>
                    }
                  </div>

                  <select
                    value={pf.assignedSlot}
                    onChange={e => {
                      const val = e.target.value;
                      setFiles(prev => prev.map((x, idx) => idx === i ? { ...x, assignedSlot: val } : x));
                      if (val !== "__other__") setOtherTexts(prev => { const n = { ...prev }; delete n[i]; return n; });
                      if (val !== "__other__" && val !== "Unlabeled" && !validateSlotFile(val, pf.file.name)) {
                        setSlotErrors(prev => ({ ...prev, [i]: slotTypeError(val) }));
                      } else {
                        setSlotErrors(prev => { const n = { ...prev }; delete n[i]; return n; });
                      }
                    }}
                    disabled={uploading}
                    className="filter-select"
                    style={{ fontSize: "12px", background: C.surface, border: `1px solid ${slotErrors[i] ? "rgba(248,113,113,0.4)" : "var(--border-2)"}`, borderRadius: "6px", color: C.text1, padding: "4px 8px", cursor: "pointer", flexShrink: 0 }}
                  >
                    {ALL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                    <option value="__other__">Other…</option>
                  </select>
                  {pf.assignedSlot === "__other__" && (
                    <input
                      type="text"
                      value={otherTexts[i] || ""}
                      onChange={e => setOtherTexts(prev => ({ ...prev, [i]: e.target.value }))}
                      placeholder="File type…"
                      disabled={uploading}
                      style={{ fontSize: "12px", background: C.surface, border: "1px solid var(--border-2)", borderRadius: "6px", color: C.text1, padding: "4px 8px", outline: "none", width: "110px", flexShrink: 0, fontFamily: "inherit" }}
                    />
                  )}

                  {/* Per-file session target — flagged users only */}
                  {batchUploader && (
                    <select
                      value={pf.sessionId || ""}
                      onChange={e => {
                        const val = e.target.value;
                        setFiles(prev => prev.map((x, idx) => idx === i ? { ...x, sessionId: val } : x));
                      }}
                      disabled={uploading}
                      className="filter-select"
                      title="Which session this file uploads to"
                      style={{ fontSize: "12px", background: C.surface, border: `1px solid ${!pf.sessionId && !selectedSession ? "rgba(245,158,11,0.45)" : "var(--border-2)"}`, borderRadius: "6px", color: pf.sessionId ? C.text1 : C.text3, padding: "4px 8px", cursor: "pointer", flexShrink: 0, maxWidth: "150px" }}
                    >
                      <option value="">{selectedSession && selectedSession !== "new" ? "(use default)" : "Session…"}</option>
                      {ownSessions.length > 0 && <option disabled>── Yours ──</option>}
                      {ownSessions.map(s => <option key={s.sessionId} value={s.sessionId}>{s.title}</option>)}
                      {partnerSessions.length > 0 && <option disabled>── Shared ──</option>}
                      {partnerSessions.map(s => <option key={s.sessionId} value={s.sessionId}>{s.title} (P{s.creatorPid})</option>)}
                    </select>
                  )}

                  {/* Retry a failed file (after the batch finishes) */}
                  {!uploading && fileProgress[i] === -1 && jobMeta[i] && (
                    <Btn variant="neutral" size="sm" onClick={() => retryFile(i)} loading={retrying[i]} style={{ flexShrink: 0 }}>
                      {retrying[i] ? "…" : "Retry"}
                    </Btn>
                  )}

                  {!uploading && (
                    <button
                      onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                      style={{ background: "none", border: "none", color: C.text3, cursor: "pointer", fontSize: "20px", padding: 0, lineHeight: 1, flexShrink: 0, fontFamily: "inherit" }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {/* ── Session selector ── */}
      <div style={{ ...card, padding: "24px", marginBottom: "16px" }}>
        <div style={{ fontSize: "11px", fontWeight: "700", color: C.text2, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "12px" }}>
          {batchUploader ? "Default Session (or assign per-file above)" : "Map to Session"}
        </div>
        {batchUploader && unassignedCount > 0 && !selectedSession && (
          <div style={{ fontSize: "12px", color: C.warning, marginBottom: "10px" }}>
            ⚠ {unassignedCount} file{unassignedCount !== 1 ? "s" : ""} {unassignedCount !== 1 ? "have" : "has"} no session — assign each above, or pick a default here.
          </div>
        )}
        <select
          value={selectedSession}
          onChange={e => { setSelectedSession(e.target.value); setNewTitle(""); setNewProject(""); }}
          disabled={uploading}
          className="filter-select"
          style={{ ...inputStyle, width: "100%", boxSizing: "border-box", color: selectedSession ? C.text1 : C.text3, cursor: "pointer", marginBottom: isNew ? "12px" : 0 }}
        >
          <option value="" disabled>Select an existing session…</option>
          <option value="new">＋ Create new session</option>
          {ownSessions.length > 0 && <option disabled>── Your sessions ──</option>}
          {ownSessions.map(s => (
            <option key={s.sessionId} value={s.sessionId}>
              {s.title} — {s.sessionId}
            </option>
          ))}
          {partnerSessions.length > 0 && <option disabled>── Shared with you ──</option>}
          {partnerSessions.map(s => (
            <option key={s.sessionId} value={s.sessionId}>
              {s.title} — {s.sessionId} (by PID {s.creatorPid})
            </option>
          ))}
        </select>

        {isNew && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ display: "flex", gap: "12px" }}>
              <input
                type="text"
                placeholder="Session title (e.g. Japanese Milk Bread)"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                disabled={uploading}
                style={{ ...inputStyle, flex: 1 }}
              />
              <select
                value={newProject}
                onChange={e => {
                  const next = e.target.value;
                  setNewProject(next);
                  if (next && !userEditedNumber) {
                    const count = sessions.filter(s => s.project === next && s.tbdTaskId).length;
                    setNewSubmissionNumber(String(400 + count + 1));
                  }
                }}
                disabled={uploading}
                className="filter-select"
                style={{ ...inputStyle, color: newProject ? C.text1 : C.text3, cursor: "pointer", minWidth: "160px" }}
              >
                <option value="" disabled>Project…</option>
                {projects.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <div>
                <input
                  type="text"
                  placeholder="Co-creator PID — e.g. P163 (optional)"
                  value={newSecondCreatorPid}
                  onChange={e => { setNewSecondCreatorPid(e.target.value); setSecondCreatorError(""); }}
                  disabled={uploading}
                  style={{ ...inputStyle, width: "220px", ...(secondCreatorError ? { border: "1px solid rgba(248,113,113,0.5)" } : {}) }}
                />
                {secondCreatorError
                  ? <div style={{ fontSize: "11px", color: C.danger, marginTop: "4px" }}>{secondCreatorError}</div>
                  : <div style={{ fontSize: "11px", color: C.text3, marginTop: "4px" }}>Credited on the data record.</div>
                }
              </div>
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Submission # (auto)"
                  value={newSubmissionNumber}
                  onChange={e => { setNewSubmissionNumber(e.target.value.replace(/[^0-9]/g, "")); setUserEditedNumber(true); }}
                  disabled={uploading}
                  style={{ ...inputStyle, width: "160px" }}
                />
                <div style={{ fontSize: "11px", color: C.text3, marginTop: "4px", lineHeight: "1.5" }}>
                  {newProject
                    ? <>Preview: <span style={{ fontFamily: "monospace", color: C.text2 }}>{`${projectCode(newProject)}_P${stripPidPrefix(pid)}_P${(stripPidPrefix(newSecondCreatorPid) || stripPidPrefix(pid))}_${newSubmissionNumber || "<num>"}`}</span></>
                    : "Pick a project to preview the ID."}
                </div>
              </div>
            </div>
            {/* Share toggle — full width below the row */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "4px", cursor: newSecondCreatorPid.trim() && !uploading ? "pointer" : "not-allowed", opacity: newSecondCreatorPid.trim() ? 1 : 0.45 }}>
              <input
                type="checkbox"
                checked={shareWithCoCreator}
                onChange={e => setShareWithCoCreator(e.target.checked)}
                disabled={!newSecondCreatorPid.trim() || uploading}
                style={{ marginTop: "2px", cursor: "inherit", accentColor: "var(--og-accent)" }}
              />
              <span style={{ fontSize: "12px", color: C.text3, lineHeight: "1.5" }}>
                Share this session with them — sends a portal invite so they can upload files from their own dashboard.
              </span>
            </label>
          </div>
        )}

        {chosenSession && (
          <div style={{ marginTop: "12px", padding: "10px 14px", background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.12)", borderRadius: "8px", display: "flex", gap: "20px", fontSize: "12px", flexWrap: "wrap" }}>
            <span style={{ color: C.text3 }}>Project: <span style={{ color: C.text2, fontWeight: "500" }}>{chosenSession.project}</span></span>
            <span style={{ color: C.text3 }}>Status: <span style={{ color: C.text2, fontWeight: "500" }}>{chosenSession.status}</span></span>
            <span style={{ color: C.text3, fontFamily: "monospace" }}>{chosenSession.sessionId}</span>
          </div>
        )}
      </div>

      {/* ── Upload button ── */}
      <Btn
        variant="primary"
        onClick={handleUploadAll}
        disabled={!canUpload}
        loading={uploading}
        style={{ width: "100%", padding: "14px" }}
      >
        {uploading
          ? `Uploading… (${doneCount}/${files.length} done · ${CONCURRENCY} at a time)`
          : `Upload & Accept All Files${files.length ? ` (${files.length})` : ""}`}
      </Btn>
    </div>
  );
}

export default BatchUpload;
